import React, { useState, useEffect, useMemo } from 'react';
import FormTable from '../../../../common/Form/Table';
import Select from '../../../../common/Form/Select';
import Input from '../../../../common/Form/Input';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useFormContext } from 'react-hook-form';
import Checkbox from '../../../../common/Form/Checkbox';
import { actualValueForDropdown, validateNumberValue } from '../../../../utils/common';
import moment from 'moment';
import DatePicker from '../../../../common/Form/DatePicker';
import IconToolTip from '../../../../common/IconToolTip';
import AssetType from '../../../../components/Inputs/AssetType';
import Categories from '../../../../components/Inputs/Categories';
import ButtonScogoPrimary from '../../../../common/Buttons/ButtonScogoPrimary';
import { addLineItemsToPO, deleteLineItemsToPO, editLineItemsToPO } from '../../../../actions/payments';
import FullScreenLoader from '../../../../components/FullScreenLoader';
import { closeModalAction, openCustomModal } from '../../../../actions/modal';
import Form from '../../../../common/Form';
import Upload from '../../../../common/Form/FileUpload';
import { motion } from 'framer-motion';
import { payToVendorsWallet, payToVendorsBank, payManually } from '../../../../actions/payments';
import { PaymentChannel, showPayPoButtons, TransactionStatus } from '../../PaymentController';
import { convertIntoPaisa, convertIntoRupee } from '../../../../utils/utils';

export const UpdatePOTable = ({ poData, line_items, vendors, arrayName, ticket, poCategoryOptions, assetTypeOptions }) => {
    const { getValues, setValue } = useFormContext();
    const [totalRowPrice, setTotalRowPrice] = useState({});
    const [isRecurringSelected, setIsRecurringSelected] = useState({});
    const [activeCopyItemId, setActiveCopyItemId] = useState(undefined);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const copyTxnDetailsLoading = useSelector((state) => state.loading.copyTxnDetailsLoading);
    const [editing, setEditing] = useState({});

    useEffect(() => {
        if (!copyTxnDetailsLoading) {
            setActiveCopyItemId(undefined);
        }
        return () => {
            if (!copyTxnDetailsLoading && !['', undefined, null].includes(activeCopyItemId)) {
                setActiveCopyItemId(undefined);
            }
        };
    }, [copyTxnDetailsLoading, activeCopyItemId]);


    useEffect(() => {
        if (line_items) {
            setValue(arrayName, line_items.map((item) => {
                return {
                    ...item,
                    end_date: item.end_date ? new Date(item.end_date) : null,
                    unit_price: convertIntoRupee(item.unit_price),
                    total_price: convertIntoRupee(item.total_price),
                    sub_total: convertIntoRupee(item.sub_total),
                }
            }));
            setTotalRowPrice(() => {
                return line_items.reduce((acc, item) => {
                    return {
                        ...acc,
                        [item.id]: item.quantity * item.unit_price,
                    }
                }, {});
            })
        }
    }, [line_items, setValue, arrayName]);

    const schema = useMemo(() => {
        return {
            table: [
                {
                    head: {
                        thClassName: `pl-5 w-36`,
                        headName: 'Asset Type',
                        required: true,
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return <AssetType name={`${arrayName}[${rowIndex}].item`} required org_id={poData.org_id} prefill={quote.item_id} />
                            } else {
                                return <div className='pl-5'>{quote.item_title}</div>;
                            }
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-36`,
                        headName: 'Category',
                        required: true,
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return <Categories name={`${arrayName}[${rowIndex}].category`} required org_id={poData.org_id} prefill={quote.category} />
                            } else {
                                return <div>{quote.category}</div>;
                            }
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-36`,
                        headName: 'Description',
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return <Input type='text' name={`${arrayName}[${rowIndex}].description`} placeholder='Description' />;
                            } else {
                                return <div>{quote.description}</div>;
                            }
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-36`,
                        headName: 'Recurring',
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return (
                                    <Checkbox
                                        type='checkbox'
                                        name={`${arrayName}[${rowIndex}].is_recurring`}
                                        onChange={(event) => {
                                            setIsRecurringSelected((prevState) => ({ ...prevState, [rowIndex]: event.target.checked }));
                                        }}
                                    />
                                );
                            } else {
                                return <div>{quote?.is_recurring ? 'Yes' : 'No'}</div>;
                            }
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-36`,
                        headName: 'End Date',
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return <DatePicker name={`${arrayName}[${rowIndex}].end_date`} placeholder='End Date' readOnly={!isRecurringSelected[rowIndex]} padding='px-0 py-1' />;
                            } else {
                                return <div>{quote.end_date && moment(quote.end_date).format('DD MMM YYYY')}</div>;
                            }
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-32`,
                        headName: 'Quantity',
                        required: true,
                    },
                    body: {
                        bodyClassName: `truncate w-32`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || editing[rowIndex]) {
                                return (
                                    <Input
                                        type='number'
                                        name={`${arrayName}[${rowIndex}].quantity`}
                                        required
                                        placeholder='Quantity'
                                        onChange={() => {
                                            const formValues = getValues();
                                            const currentRowValues = formValues[arrayName][rowIndex];
                                            let quantity = 1;
                                            if (validateNumberValue(currentRowValues.quantity) > 0) quantity = validateNumberValue(currentRowValues.quantity);
                                            setTotalRowPrice((prev) => {
                                                return {
                                                    ...prev,
                                                    [quote.id]: quantity * currentRowValues.unit_price
                                                }
                                            });
                                        }}
                                    />
                                );
                            }
                            return <div className=''>{quote.quantity}</div>;
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-32`,
                        headName: `Price`,
                        required: true,
                        isVendor: true,
                        vendor: poData?.vendor,
                    },
                    body: {
                        bodyClassName: `truncate w-32`,
                        render: (quote, { rowIndex }) => {
                            if (quote.isNew || (editing[rowIndex])) {
                                return (
                                    <Input
                                        type='number'
                                        step='any'
                                        name={`${arrayName}[${rowIndex}].unit_price`}
                                        required
                                        onChange={(event) => {
                                            const formValues = getValues();
                                            const currentRowValues = formValues[arrayName][rowIndex];
                                            let quantity = 1;
                                            if (validateNumberValue(currentRowValues.quantity) > 0) quantity = validateNumberValue(currentRowValues.quantity);
                                            setTotalRowPrice((prev) => {
                                                return {
                                                    ...prev,
                                                    [quote.id]: quantity * currentRowValues.unit_price
                                                }
                                            });
                                        }}
                                    />
                                );
                            }
                            return <div className=''>{quote.unit_price}</div>;
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-36`,
                        headName: 'Total Price',
                    },
                    body: {
                        bodyClassName: `truncate w-36`,
                        render: (quote, { rowIndex }) => {
                            let price = totalRowPrice[quote.id];
                            if (!price) price = quote.total_price;
                            return <div>{price}</div>;
                        },
                    },
                },
                {
                    head: {
                        thClassName: `w-96`,
                        headName: 'Status/Actions',
                    },
                    body: {
                        bodyClassName: `truncate w-96`,
                        render: (quote, { rowIndex, remove }) => {
                            let poAction = <></>;

                            poAction = <RenderPayPoAction remove={remove} arrayName={arrayName} isDeletable={quote.isDeletable} isEditable={quote.isEditable} rowIndex={rowIndex} data={quote} setActiveCopyItemId={setActiveCopyItemId} activeCopyItemId={activeCopyItemId} poId={poData.id} item={quote} editing={editing} setEditing={setEditing} line_items={line_items} />

                            return <div className='flex items-center'>
                                {poAction}
                            </div>;
                        },
                    },
                }
            ],
        };
    }, [
        arrayName,
        totalRowPrice,
        getValues,
        isRecurringSelected,
        poData?.org_id,
        poData?.vendor,
        activeCopyItemId,
        poData?.id,
        editing,
        setEditing,
        line_items
    ]);

    const isLoading = formButtonLoading['itemUpdating'];


    return (
        <>
            {isLoading ? <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
                <div className="loader">
                    <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div> : null}
            <FormTable
                schema={schema}
                addButtonClassName='my-2'
                name={arrayName}
                rowGenerator={() => ({
                    id: `${Math.random()}`,
                    isEditable: true,
                    isDeletable: true,
                    isNew: true
                })}
                scrollToBottomOnAddRow
            />
            {/* <TotalCard schema={schema} arrayName={arrayName} /> */}
        </>
    );
};

const RenderPayPoAction = ({ isEditable, rowIndex, poId, arrayName, remove, item, editing, setEditing, line_items }) => {
    const { getValues, setValue } = useFormContext();
    const dispatch = useDispatch();
    const formName = `itemUpdating`;
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const currentData = getValues(arrayName)[rowIndex];
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const onSave = () => {

        if (editing[rowIndex] === true) {
            let payload = {
                type: "PO_ITEM",
                category: actualValueForDropdown(currentData.category),
                item_id: currentData.item_id,
                item_title: currentData.item_title,
                item_alias: currentData.item_alias || "",
                description: currentData.description,
                quantity: parseFloat(currentData.quantity),
                unit: currentData.unit || "INR",
                currency: currentData.currency || "INR",
                unit_price: convertIntoPaisa(currentData.unit_price),
                start_date: currentData.start_date,
                is_recurring: currentData.is_recurring,
                cycle_type: currentData.cycle_type || "MONTHLY",
                cycle_interval: currentData.cycle_interval || 0,
                end_date: currentData.end_date,
                attributes: currentData.attributes || [],
                metadata: currentData.metadata || {}
            };
            dispatch(editLineItemsToPO({ formName, id: poId, itemId: currentData.id, payload }));
        } else {
            const payload = {
                type: 'PO_ITEM',
                category: actualValueForDropdown(currentData.category),
                item_id: currentData.item?.value,
                item_title: currentData.item?.label,
                description: currentData.description,
                quantity: parseFloat(currentData.quantity),
                unit: 'INR',
                currency: "INR",
                unit_price: convertIntoPaisa(currentData.unit_price),
                start_date: currentData.startDate,
                is_recurring: currentData.is_recurring,
                end_date: currentData.end_date,
            };
            dispatch(addLineItemsToPO({ formName, id: poId, payload }));
        }
    };

    const onEdit = () => {
        setEditing((prevState) => ({ ...prevState, [rowIndex]: true }));
    };

    const onCancelEdit = () => {
        // Reset the form values to original state
        const formValues = getValues();
        const originalItem = line_items.find(item => item.id === currentData.id);

        if (originalItem) {
            // Reset to original values
            const resetValues = {
                ...originalItem,
                end_date: originalItem.end_date ? new Date(originalItem.end_date) : null,
                unit_price: convertIntoRupee(originalItem.unit_price),
                total_price: convertIntoRupee(originalItem.total_price),
                sub_total: convertIntoRupee(originalItem.sub_total),
            };

            // Update the specific row in the form array
            const updatedArray = [...formValues[arrayName]];
            updatedArray[rowIndex] = resetValues;
            setValue(arrayName, updatedArray);
        }

        setEditing((prevState) => ({ ...prevState, [rowIndex]: false }));
    };

    const onDelete = () => {
        remove(rowIndex);
        if (!isEditable) {
            dispatch(deleteLineItemsToPO({ formName, id: poId, itemId: currentData.id }));
        }
    }

    const onPayClick = (type) => {
        dispatch(openCustomModal({
            modalComponent: <WalletForm type={type} itemId={currentData.id} id={poId} />,
            heading: 'Payment Confirmation',
            modalWidth: '40rem',
            modalHeight: 'auto',
        }))
    }

    const showEditButton = !item.isNew && !formButtonLoading[formName] && !editing[rowIndex];
    const showSaveButton = (item.isNew || editing[rowIndex] === true) && !formButtonLoading[formName];
    const showPaymentButtons = showPayPoButtons({ payment_status: item.payment_status, newLoggedUserData }) && !showSaveButton;

    if (item.payment_status === TransactionStatus.SUCCESS) {
        return <RenderStatus item={item} />
    }

    return (
        <>
            <div className="flex gap-2 w-full">
                {showEditButton && (

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            onEdit(rowIndex);
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
                    >
                        <span className="material-icons text-font12">edit</span>
                        <span>Edit</span>
                    </button>

                )}
                {showSaveButton && (

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            onSave(rowIndex);
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
                    >
                        <span className="material-icons text-font12">save</span>
                        <span>Save</span>
                    </button>

                )}
                {editing[rowIndex] && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            onCancelEdit();
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
                    >
                        <span className="material-icons text-font12">cancel</span>
                        <span>Cancel</span>
                    </button>

                )}
                {showPaymentButtons && <RenderPaymentButtons onPayClick={onPayClick} onDelete={onDelete} />}
                {!showPaymentButtons && <button
                    onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                    }}
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 transition-colors"
                >
                    <span className="material-icons text-scogoToastDangerIcon text-font14">delete</span>
                </button>}
            </div>
        </>
    );
}

const RenderStatus = ({ item }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case TransactionStatus.SUCCESS:
                return 'bg-green-100 text-green-800';
            case TransactionStatus.FAILURE:
            case TransactionStatus.REJECTED:
                return 'bg-red-100 text-red-800';
            case TransactionStatus.PENDING:
            case TransactionStatus.INITIATED:
                return 'bg-yellow-100 text-yellow-800';
            case TransactionStatus.PROCESSING:
            case TransactionStatus.SCHEDULED:
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getChannelIcon = (channel) => {
        switch (channel) {
            case PaymentChannel.WALLET:
                return 'account_balance_wallet';
            case PaymentChannel.BANK:
                return 'account_balance';
            case PaymentChannel.OFFLINE:
                return 'payments';
            default:
                return 'payment';
        }
    };

    return (
        <div className="flex gap-1">
            {item.payment_status && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-font10 font-medium ${getStatusColor(item.payment_status)}`}>
                    {item.payment_status}
                </span>
            )}
            {item.payment_channel && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-font10 font-medium ${getChannelIcon(item.payment_channel)}`}>
                    <span className="material-icons text-font12 mr-1">{getChannelIcon(item.payment_channel)}</span>
                    {item.payment_channel}
                </span>
            )}

        </div>
    );
}

const RenderPaymentButtons = ({ onPayClick, onDelete }) => {
    return <>
        <button
            onClick={() => onPayClick('wallet')}
            className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
        >
            <span className="material-icons text-font12">account_balance_wallet</span>
            <span>Wallet</span>
        </button>
        <button
            onClick={() => onPayClick('bank')}
            className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
        >
            <span className="material-icons text-font12">account_balance</span>
            <span>Bank</span>
        </button>
        <button
            onClick={() => onPayClick('manual')}
            className="flex items-center gap-1 px-2 py-1 bg-scogoprimary text-white rounded-md hover:bg-scogoprimary-dark transition-colors text-xs"
        >
            <span className="material-icons text-font12">payments</span>
            <span>Manually</span>
        </button>

        <button
            onClick={(e) => {
                e.preventDefault();
                onDelete();
            }}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 transition-colors"
        >
            <span className="material-icons text-scogoToastDangerIcon text-font14">delete</span>
        </button>
    </>
}

const WalletForm = ({ type, itemId, id }) => {
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const methods = useForm();
    const formName = `${id}-${itemId}-${type}`;
    const dispatch = useDispatch();

    const onSubmit = (data, type) => {
        if (type === 'wallet') {
            dispatch(payToVendorsWallet({ id, itemId, payload: { remark: data.remark }, type: 'wallet', formName }));
        }

        if (type === 'bank') {
            dispatch(payToVendorsBank({ id, itemId, payload: { remark: data.remark }, type: 'bank', formName }));
        }

        if (type === 'manual') {
            dispatch(payManually({ id, itemId, payload: { remark: data.remark, file: Array.from(data.payment_proof)[0] }, type: 'manual', formName }));
        }
    }

    if (type === 'wallet') {
        return (
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className='p-4 overflow-hidden'
            >
                <motion.p
                    className='text-scogogray text-font12'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.15 }}
                >
                    Are you sure you want to pay to wallet?
                </motion.p>
                <Form
                    methods={methods}
                    onSubmit={(data) => onSubmit(data, 'wallet')}
                    submitButtonText="Confirm Payment"
                    onCancel={() => dispatch(closeModalAction())}
                    showSubmit
                    submitContainerClassName="flex gap-2 justify-end py-3"
                    isLoading={formButtonLoading[formName]}
                >
                    <Input name="remark" type="text" placeholder="Remark" />
                </Form>
            </motion.div>
        );
    }

    if (type === 'bank') {
        return (
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className='p-4 overflow-hidden'
            >
                <motion.p
                    className='text-scogogray text-font12'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.15 }}
                >
                    Are you sure you want to pay to bank?
                </motion.p>
                <Form
                    methods={methods}
                    onSubmit={(data) => onSubmit(data, 'bank')}
                    submitButtonText="Confirm Payment"
                    onCancel={() => dispatch(closeModalAction())}
                    showSubmit
                    submitContainerClassName="flex gap-2 justify-end py-3"
                    isLoading={formButtonLoading[formName]}
                >
                    <Input name="remark" type="text" placeholder="Remark" />
                </Form>
            </motion.div>
        );
    }

    if (type === 'manual') {
        return (
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className='p-4 overflow-hidden'
            >
                <motion.p
                    className='text-scogogray text-font12'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.15 }}
                >
                    Are you sure you want to pay manually?
                </motion.p>
                <Form
                    methods={methods}
                    onSubmit={(data) => onSubmit(data, 'manual')}
                    submitButtonText="Confirm Payment"
                    onCancel={() => dispatch(closeModalAction())}
                    showSubmit
                    submitContainerClassName="flex gap-2 justify-end py-3"
                    isLoading={formButtonLoading[formName]}
                >
                    <div className='flex gap-2'>
                        <Upload className='w-full' name="payment_proof" required />
                        <Input className='w-full' name="remark" type="text" placeholder="Remark" />
                    </div>
                </Form>
            </motion.div>
        );
    }
}